import {createApi} from "@reduxjs/toolkit/query/react";
import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {appendFormData} from "@/helpers/form";

export const productsApi = createApi({
    reducerPath: 'productsApi',
    tagTypes: ['products'],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: build => ({
        getProducts: build.query({
            query: ({location_id, storage_id, order_by, order_direction, skip}) => {
                return {
                    url: api.products.getProducts(),
                    method: 'GET',
                    params: {location_id, storage_id, skip, order_by, order_direction}
                }
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id, queryArgs?.storage_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ['products']
        }),
        getProductOptions: build.query({
            query: (data) => {
                return {
                    url: api.products.getProductsOptions(),
                    method: 'GET',
                    params: data
                }
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
        }),
        createProduct: build.mutation({
            query: (data) => ({
                url: api.products.createProduct(),
                method: 'POST',
                body: data
            }),
            async onQueryStarted(newProduct, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    const changedData = {
                        ...data, ...newProduct
                    }
                    dispatch(
                        productsApi.util.updateQueryData('getProductOptions', {location_id: newProduct.location_id}, (draft) => {
                            draft.unshift({
                                ...changedData,
                                critical_stock: newProduct.critical_stock * newProduct.usable_count
                            });
                        })
                    );
                } catch (error) {
                    console.error('Failed to update cache after creating product:', error);
                }
            }
        }),
        editProduct: build.mutation({
            query: ({id, ...data}) => ({
                url: api.products.updateProduct(id),
                method: 'PUT',
                body: data
            }),
            async onQueryStarted(updatedProduct, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(
                        productsApi.util.updateQueryData('getProductOptions', {location_id: updatedProduct.location_id}, (draft) => {
                            const index = draft.findIndex(item => {
                                return item.id === updatedProduct.id
                            });
                            if (index !== -1) {
                                draft[index] = {
                                    ...draft[index],
                                    ...updatedProduct,
                                    critical_stock: updatedProduct.critical_stock * updatedProduct.usable_count
                                };
                            }
                        })
                    );
                } catch (error) {
                    console.error('Failed to update cache after editing product:', error);
                }
            }
        }),
        importProducts: build.mutation({
            query: (data) => {
                const formData = new FormData();
                appendFormData(formData, data);

                return {
                    url: api.products.importProducts(),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data;'
                    },
                    body: formData,
                    formData: true
                }
            },
            invalidatesTags: ['products'],
        }),
        deleteProduct: build.mutation({
            query: ({id, ...data}) => ({
                url: api.products.deleteProduct(id),
                method: 'DELETE',
                params: data,
            }),
            async onQueryStarted(patch, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    dispatch(
                        productsApi.util.updateQueryData('getProductOptions', {location_id: patch.location_id}, (draft) => {
                            const index = draft.findIndex((product) => product.id === patch.id);
                            if (index !== -1) {
                                draft.splice(index, 1)
                            } else {
                                console.warn('Product not found in cache')
                            }
                        })
                    );
                } catch (e) {
                    console.error('Delete failed:', e)
                }
            },
            invalidatesTags: ['products'],
        }),
        getProductHistory: build.query({
            query: ({id, ...data}) => ({
                url: api.products.getProductHistory(id),
                method: 'GET',
                params: data,
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id, queryArgs?.storage_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
        }),
    })
})

export const {useLazyGetProductOptionsQuery, useLazyGetProductHistoryQuery} = productsApi
